import { Warning as WarningIcon } from "@mui/icons-material";
import { Alert, Box, Button, Typography } from "@mui/material";
import React from "react";

interface SkipActionsProps {
  onSkipActionChange: (skipAction: SkipActionType | null) => void;
  selectedSkipAction: SkipActionType | null;
  verification?: InsuranceVerificationMessage | null;
}

const getBennyRecommendation = (
  verification?: InsuranceVerificationMessage | null,
) => {
  if (!verification) {
    return { show: false, message: "" };
  }

  // Don't show Benny recommendations for completed verifications
  if (verification.verificationStatus === "COMPLETED") {
    return { show: false, message: "" };
  }

  const isMissingInsuranceCompany =
    !verification.insuranceCompany ||
    verification.insuranceCompany.trim() === "";

  if (isMissingInsuranceCompany) {
    return {
      show: true,
      message: `"Insufficient Data" - I noticed this verification is missing the insurance company - should we skip it for now?`,
    };
  }

  return { show: false, message: "" };
};

export default function SkipActions({
  onSkipActionChange,
  selectedSkipAction,
  verification,
}: SkipActionsProps) {
  const bennyRecommendation = getBennyRecommendation(verification);
  const [hasUserInteracted, setHasUserInteracted] = React.useState(false);

  React.useEffect(() => {
    if (bennyRecommendation.show && !selectedSkipAction && !hasUserInteracted) {
      onSkipActionChange("INSUFFICIENT_DATA");
    }
  }, [
    bennyRecommendation.show,
    selectedSkipAction,
    onSkipActionChange,
    hasUserInteracted,
  ]);

  const handleSkipButtonClick = (skipActionType: SkipActionType) => {
    setHasUserInteracted(true);
    if (selectedSkipAction === skipActionType) {
      onSkipActionChange(null);
    } else {
      onSkipActionChange(skipActionType);
    }
  };

  const getButtonStyles = (skipActionType: SkipActionType) => {
    const isSelected = selectedSkipAction === skipActionType;

    return {
      borderColor: isSelected ? "#1976d2" : "#e0e0e0",
      color: isSelected ? "white" : "text.primary",
      backgroundColor: isSelected ? "#1976d2" : "transparent",
      "&:hover": {
        borderColor: isSelected ? "#1565c0" : "#bdbdbd",
        backgroundColor: isSelected ? "#1565c0" : "#f5f5f5",
      },
    };
  };

  return (
    <Box
      sx={{
        backgroundColor: "#fff8e1",
        borderRadius: 2,
        p: 3,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <WarningIcon sx={{ color: "#f57c00", mr: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#f57c00" }}>
          Skip This Verification?
        </Typography>
      </Box>

      <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
        If you don&apos;t need to verify this appointment, select a reason:
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Button
          variant={
            selectedSkipAction === "ALREADY_VERIFIED" ? "contained" : "outlined"
          }
          onClick={() => handleSkipButtonClick("ALREADY_VERIFIED")}
          sx={getButtonStyles("ALREADY_VERIFIED")}
          data-testid="alreadyVerified"
        >
          Already Verified
        </Button>

        <Button
          variant={
            selectedSkipAction === "NOT_REQUIRED" ? "contained" : "outlined"
          }
          onClick={() => handleSkipButtonClick("NOT_REQUIRED")}
          sx={getButtonStyles("NOT_REQUIRED")}
          data-testid="notRequired"
        >
          Not Required
        </Button>

        <Button
          variant={
            selectedSkipAction === "SECONDARY_INSURANCE"
              ? "contained"
              : "outlined"
          }
          onClick={() => handleSkipButtonClick("SECONDARY_INSURANCE")}
          sx={getButtonStyles("SECONDARY_INSURANCE")}
          data-testid="secondaryInsurance"
        >
          Secondary Insurance
        </Button>

        <Button
          variant={
            selectedSkipAction === "INSUFFICIENT_DATA"
              ? "contained"
              : "outlined"
          }
          onClick={() => handleSkipButtonClick("INSUFFICIENT_DATA")}
          sx={getButtonStyles("INSUFFICIENT_DATA")}
          data-testid="insufficientData"
        >
          Insufficient Data
        </Button>
      </Box>

      {bennyRecommendation.show && (
        <Alert
          severity="info"
          sx={{
            mt: 3,
            backgroundColor: "#e3f2fd",
            border: "1px solid #bbdefb",
            "& .MuiAlert-icon": {
              color: "#1976d2",
            },
          }}
        >
          <Typography variant="body2">
            <strong>Benny&apos;s recommendation:</strong>{" "}
            {bennyRecommendation.message}
          </Typography>
        </Alert>
      )}
    </Box>
  );
}
